import dayjs from "dayjs";
import { formatNumber } from "chartjs-graph";
import { Action, Condition } from "@/modules/rule_engine/ruleEngineClasses";
import ABTestDTO = App.Data.ABTest.ABTestDTO;

/**
 * Human-readable rendering of rules, as data.
 *
 * Returning segments rather than a string lets every surface — the rule list,
 * the group preview, the version comparison — style the same description
 * consistently, and keeps the logic testable without mounting a component.
 */

export type SegmentKind =
	| "keyword"
	| "dimension"
	| "operator"
	| "value"
	| "text";

export type Segment = { kind: SegmentKind; text: string };

/** Matches vue-i18n's `t`, so components can pass it straight through. */
export type Translate = (key: string) => string;

/** Context a condition or action can't resolve on its own. */
export type DescribeContext = {
	abTests?: ABTestDTO[];
	actionValues?: Partial<
		Record<Action["type"], { uuid: string; name: string }[]>
	>;
	/** Keyed by dimension uuid; lets selected IDs render as their names. */
	dimensionValues?: Record<
		string,
		{ display_name: string; value: string | null }[] | undefined
	>;
};

const segment = (kind: SegmentKind, text: string): Segment => ({ kind, text });

const UNKNOWN = "—";

/** Dates are stored ISO; show them the way people read them. */
const formatDate = (value: string): string => {
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format("D MMM YYYY") : String(value ?? "");
};

/**
 * Condition values carry no unit metadata from the backend, so numbers are
 * rendered as-is rather than guessed at (a 0.7 occupancy threshold cannot be
 * distinguished from a literal 0.7 here).
 */
const formatConditionValue = (value: unknown): string =>
	value === null || value === undefined ? "" : String(value);

const formatSelectionValues = (
	condition: Extract<
		Condition,
		{ type: "in_number" | "in_string" | "not_in_number" | "not_in_string" }
	>,
	context: DescribeContext
): string => {
	const options =
		context.dimensionValues?.[condition.dimension?.uuid ?? ""] ?? [];

	const names = condition.values.map(
		(value) =>
			options.find((option) => option.value === String(value))?.display_name ??
			formatConditionValue(value)
	);

	return [...new Set(names)].join(", ");
};

const dimensionLabel = (condition: Condition, t: Translate): string => {
	const name = condition.dimension?.name_clean;
	return name ? t(`rule_engine.rule_dimensions.${name}.title`) : UNKNOWN;
};

const conditionTypeLabel = (condition: Condition, t: Translate): string =>
	t(`rule_engine.condition_types.${condition.type}`);

const describeAbTest = (
	condition: Extract<Condition, { type: "ab_test" }>,
	t: Translate,
	context: DescribeContext
): Segment[] => {
	const abTest = context.abTests?.find(
		(candidate) => candidate.uuid === condition.dimension?.ab_test_uuid
	);

	if (!abTest) {
		return [segment("dimension", t("rule_engine.ab_test.unknown_test"))];
	}

	const variantNames = (abTest.variants ?? [])
		.filter((variant) => condition.values.includes(variant.uuid))
		.map((variant) => variant.name);

	return [
		segment("dimension", abTest.name),
		segment(
			"operator",
			variantNames.length === 1
				? t("rule_engine.ab_test.is_variant")
				: t("rule_engine.ab_test.is_one_of_variants")
		),
		segment("value", variantNames.join(", ")),
	];
};

export const describeCondition = (
	condition: Condition,
	t: Translate,
	context: DescribeContext = {}
): Segment[] => {
	// Nothing chosen yet, so there is nothing to describe — prompt instead.
	if (condition.type === "draft") {
		return [segment("text", t("rule_engine.condition_draft"))];
	}

	if (condition.type === "ab_test") {
		return describeAbTest(condition, t, context);
	}

	const head = [
		segment("dimension", dimensionLabel(condition, t)),
		segment("operator", conditionTypeLabel(condition, t)),
	];

	switch (condition.type) {
		case "in_number":
		case "in_string":
		case "not_in_number":
		case "not_in_string":
			return [
				...head,
				segment("value", formatSelectionValues(condition, context)),
			];

		case "day_of_week":
			return [
				...head,
				segment(
					"value",
					condition.values
						.map((day) => t(`rule_engine.days_of_week.${day}`))
						.join(", ")
				),
			];

		case "time_of_day":
			return [
				...head,
				segment(
					"value",
					(condition.value?.times ?? [])
						.map((range) => `${range.start} – ${range.end}`)
						.join(", ")
				),
			];

		case "comparison_boolean":
			return [
				...head,
				segment(
					"operator",
					t(`rule_engine.condition_operators.${condition.operator}`)
				),
				segment(
					"value",
					t(`rule_engine.boolean_values.${condition.value ? "true" : "false"}`)
				),
			];

		case "comparison_date":
			return [
				...head,
				segment(
					"operator",
					t(`rule_engine.condition_operators.${condition.operator}`)
				),
				segment("value", formatDate(condition.value)),
			];

		case "comparison_number":
		case "comparison_string":
			return [
				...head,
				segment(
					"operator",
					t(`rule_engine.condition_operators.${condition.operator}`)
				),
				segment("value", formatConditionValue(condition.value)),
			];

		case "between_date":
			return [
				...head,
				segment("value", formatDate(condition.from)),
				segment("text", t("rule_engine.and")),
				segment("value", formatDate(condition.to)),
			];

		case "between_number":
			return [
				...head,
				segment("value", formatConditionValue(condition.from)),
				segment("text", t("rule_engine.and")),
				segment("value", formatConditionValue(condition.to)),
			];

		case "in_hierarchy":
		case "not_in_hierarchy":
			return [
				...head,
				segment(
					"value",
					condition.scope?.label ?? t("rule_engine.hierarchy.whole_tree")
				),
				...(condition.excluded_branches.length > 0
					? [
							segment("text", t("rule_engine.hierarchy.excluding")),
							segment("value", String(condition.excluded_branches.length)),
							segment("text", t("rule_engine.hierarchy.excluded_branches")),
					  ]
					: []),
			];

		default:
			return head;
	}
};

export const describeAction = (
	action: Action,
	t: Translate,
	context: DescribeContext = {}
): Segment[] => {
	// Narrow positively: the modify types share one union member, so excluding
	// their discriminant values one at a time does not remove the member.
	if (
		action.type === "add_task_to_bex_reservation" ||
		action.type === "add_label_to_bex_reservation"
	) {
		const targetUuid =
			action.type === "add_task_to_bex_reservation"
				? action.task_id
				: action.label_id;

		const match = context.actionValues?.[action.type]?.find(
			(candidate) => candidate.uuid === targetUuid
		);

		return [
			segment("keyword", t(`rule_engine.action_modifier_types.${action.type}`)),
			segment("value", match?.name ?? String(targetUuid ?? UNKNOWN)),
		];
	}

	if (action.type === "set_absolute") {
		return [
			segment("keyword", t("rule_engine.action_set")),
			segment(
				"dimension",
				t(`rule_engine.action_variables.${action.variable}`)
			),
			segment("text", t("rule_engine.to")),
			segment("value", formatNumber(action.amount, "default")),
		];
	}

	const isIncrease = action.amount >= 0;
	const isPercentage = action.type === "modify_percentage";

	const described = [
		segment(
			"keyword",
			isIncrease
				? t("rule_engine.action_direction.increase")
				: t("rule_engine.action_direction.reduce")
		),
		segment("dimension", t(`rule_engine.action_variables.${action.variable}`)),
		segment("text", t("rule_engine.by")),
		segment(
			"value",
			formatNumber(
				Math.abs(action.amount),
				isPercentage ? "percentage" : "default"
			)
		),
	];

	/*
	 * A percentage announces itself with its "%", an absolute amount does not.
	 * Without this the two modify types describe identically, so switching
	 * between them looks like nothing happened.
	 */
	if (!isPercentage) {
		described.push(segment("text", t("rule_engine.action_absolute_unit")));
	}

	return described;
};

/** Joins segments into a plain string, for tooltips and title attributes. */
export const segmentsToText = (segments: Segment[]): string =>
	segments
		.map((part) => part.text)
		.filter((text) => text !== "")
		.join(" ");
